import { z } from 'zod';
import { currencyCode, minorUnits, timestamp, uuid } from './common.js';
import { listQueryInput, paginated } from './list.js';
import { userTier } from './user.js';
/**
 * Money (§4, §21–§23). `credit_ledger` (pinned shape), `subscriptions`,
 * `invoices`, `promo_codes`, `promo_redemptions`, `credit_packs`,
 * `action_credit_costs`.
 */
/**
 * Closed `credit_ledger.reason` enum (§21). Sign convention noted per reason.
 * Idempotency = partial-unique `(reason, ref_id) WHERE ref_id IS NOT NULL`.
 */
export const creditReason = z.enum([
    'action_debit', // − ref=jobs.id
    'policy_refund', // + ref=jobs.id (compensating, idempotent)
    'purchase', // + ref=Paddle txn / invoices.id
    'admin_grant', // + nullable ref
    'share_approved', // + ref=shares.id
    'share_public_reward', // + ref=shares.id
    'referral_reward', // + ref=referral.id
    'promo_bonus', // + ref=promo_redemptions.id
    'signup_grant', // + ref=users.id (one-time starter credits — emails `welcome.creditsPointer`)
]);
/**
 * `credit_ledger` — append-only, single-entry, SIGNED delta. EXACT shape:
 * `(id, userId, delta, reason, refId, createdAt)`. NO `balanceAfter` column
 * (F19 computes the running balance client-side). `users.creditsBalance` is the
 * materialized SUM and the only live balance authority.
 */
export const creditLedgerSchema = z.object({
    id: uuid,
    userId: uuid,
    delta: z.number().int(), // signed
    reason: creditReason,
    refId: uuid.nullable(),
    createdAt: timestamp,
});
// --- read DTOs (credits.balance / credits.ledger, F19/F47) ------------------
/** `credits.balance` — reads `users.creditsBalance` directly; NEVER cached (§21.3/E1). */
export const creditsBalanceResult = z.object({ balance: z.number().int() });
export const creditLedgerSortColumns = ['createdAt'];
export const creditLedgerListQuery = listQueryInput(creditLedgerSortColumns, {
    defaultSort: 'createdAt',
});
/** F19 ledger table — the running balance shown alongside these rows is
 *  CLIENT-computed (never a stored/returned column, §21.4). */
export const creditLedgerListResult = paginated(creditLedgerSchema);
/**
 * Push on `user:{userId}` (§21.3/F47), fired by every ledger write in the
 * same transaction as the insert. Formalizes the shape `@xdocs/core`'s
 * `debitForAction`/`grantCredits`/`refundPolicyRejection` already emit
 * (`packages/core/src/credits.ts`'s local `CreditBalancePush`) — that
 * interface is byte-identical and is a follow-up candidate to import this
 * instead of redeclaring it.
 */
export const creditsBalanceChangedPush = z.object({
    type: z.literal('credits.balanceChanged'),
    balance: z.number().int(),
});
export const subscriptionStatus = z.enum([
    'active',
    'trialing',
    'past_due',
    'paused',
    'canceled',
]);
export const subscriptionSchema = z.object({
    id: uuid,
    userId: uuid,
    tier: userTier,
    status: subscriptionStatus,
    paddleSubscriptionId: z.string().nullable(),
    currentPeriodEnd: timestamp.nullable(),
    cancelAt: timestamp.nullable(),
    createdAt: timestamp,
    updatedAt: timestamp,
});
export const invoiceStatus = z.enum(['paid', 'open', 'void', 'refunded']);
export const invoiceSchema = z.object({
    id: uuid,
    userId: uuid,
    paddleTransactionId: z.string().nullable(),
    amount: minorUnits,
    currency: currencyCode,
    status: invoiceStatus,
    /** Presigned URL is derived on read (cached); the PDF itself is a `files` row. */
    invoiceFileId: uuid.nullable(),
    issuedAt: timestamp,
    createdAt: timestamp,
});
// --- promo codes (§22) -------------------------------------------------------
export const promoKind = z.enum(['percent_off', 'fixed_off', 'bonus_credits']);
export const promoAppliesTo = z.enum(['subscription', 'credit_pack', 'any']);
export const promoCodeSchema = z.object({
    id: uuid,
    code: z.string().min(1).max(64), // citext UNIQUE in DB
    kind: promoKind,
    valueNumeric: z.number(), // percent, minor-unit amount, or credit count per kind
    currency: currencyCode.nullable(), // fixed_off only
    maxRedemptions: z.number().int().positive().nullable(), // null = unlimited
    perUserLimit: z.number().int().positive(),
    validFrom: timestamp.nullable(),
    validUntil: timestamp.nullable(),
    appliesTo: promoAppliesTo,
    appliesToRefId: uuid.nullable(),
    redemptionCount: z.number().int().nonnegative(), // materialized
    isActive: z.boolean(),
    createdAt: timestamp,
    updatedAt: timestamp,
});
/** Append-only redemption record. */
export const promoRedemptionSchema = z.object({
    id: uuid,
    promoCodeId: uuid,
    userId: uuid,
    /** The subscription/credit_pack the code was applied to, if any. */
    appliedToRefId: uuid.nullable(),
    createdAt: timestamp,
});
// --- buy-credits packs (§23) -------------------------------------------------
export const creditPackSchema = z.object({
    id: uuid,
    slug: z.string().min(1).max(64), // UNIQUE
    creditsAmount: z.number().int().positive(),
    priceAmount: minorUnits,
    currency: currencyCode,
    paddlePriceId: z.string().min(1),
    /** Names/blurbs are localized via catalog keys by slug; this is a fallback key. */
    complexityBlurbKey: z.string().nullable(),
    isActive: z.boolean(),
    sortOrder: z.number().int(),
    createdAt: timestamp,
});
// --- cost model (§21, F48) ---------------------------------------------------
/** Chargeable action types (matches worker/job vocabulary). */
export const costActionType = z.enum([
    'generate',
    'expand',
    'cut_continue',
    'difficulty_elevate',
    'translate',
    'illustrate',
    'render',
]);
/** Locked complexity ladder (§21). */
export const complexityTier = z.enum(['outline', 'basic', 'standard', 'rich', 'mastery']);
export const costUnitLabel = z.enum(['chapter', 'language']);
export const actionCreditCostSchema = z.object({
    id: uuid,
    actionType: costActionType,
    complexityTier,
    baseCost: z.number().int().nonnegative(),
    perUnitCost: z.number().int().nonnegative(),
    unitLabel: costUnitLabel.nullable(),
    tierRequired: userTier,
    isActive: z.boolean(),
    createdAt: timestamp,
    updatedAt: timestamp,
});
/**
 * Deterministic, quotable estimate (§21) — feeds the cost chip, the F15 confirm
 * dialog ("balance → balance − credits"), and the worker debit. Quoted = charged.
 */
export const costEstimate = z.object({
    actionType: costActionType,
    complexityTier,
    units: z.number().int().nonnegative(),
    credits: z.number().int().nonnegative(),
    balance: z.number().int(),
    balanceAfter: z.number().int(),
    tierRequired: userTier,
});
// --- billing input DTOs ------------------------------------------------------
/** `promo.redeem` (§22). Authoritative check is server-side in one txn. */
export const promoRedeemInput = z.object({
    code: z.string().trim().min(1, 'errors:promo.code.required').max(64),
    appliesTo: promoAppliesTo,
    appliesToRefId: uuid.optional(),
});
/**
 * `promo.redeem` result (F56) — the resolved, discriminated effect. A
 * `percent_off`/`fixed_off` code attaches a Paddle discount to the NEXT
 * `billing.checkout.start` call (`./billing.dto.ts`) for the same
 * `appliesTo`/`appliesToRefId`; a `bonus_credits` code grants immediately via
 * `credit_ledger` (`reason='promo_bonus'`, idempotent by
 * `(reason, promo_redemptions.id)`).
 */
export const promoRedeemEffect = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('percent_off'), percent: z.number().positive().max(100) }),
    z.object({ kind: z.literal('fixed_off'), amount: minorUnits, currency: currencyCode }),
    z.object({ kind: z.literal('bonus_credits'), credits: z.number().int().positive() }),
]);
export const promoRedeemResult = z.object({
    redemptionId: uuid,
    promoCodeId: uuid,
    effect: promoRedeemEffect,
});
